import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateExecutionImage } from '@/lib/api/generated/api';
import type { ExecutionImageView } from '@/lib/api/generated/model';

import { toExecutionImageError } from '../errors';
import { executionImageKeys } from '../queryKeys';

/**
 * Edit a catalog entry's `label`, `image_reference`, and/or `connection_id` —
 * all three are editable via `ExecutionImageUpdate`; any field left unset is
 * left unchanged.
 */
export function useUpdateExecutionImage(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      imageId,
      label,
      imageReference,
      connectionId,
    }: {
      imageId: string;
      label?: string;
      imageReference?: string;
      connectionId?: string;
    }): Promise<ExecutionImageView> => {
      const response = await updateExecutionImage(workspaceId, imageId, {
        label,
        image_reference: imageReference,
        connection_id: connectionId,
      });
      if (response.status === 200) return response.data;
      throw toExecutionImageError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executionImageKeys.list(workspaceId),
      });
    },
  });
}
